import { IState } from "./../index";
import { IAuthState } from "./auth";
import { Model } from "@rematch/core";
import FirebaseAuthService from "../../../services/firebase/auth/FirebaseAuthService";
import LocalStorageService from "../../../services/LocalStorage/LocalStorageService";
import MixPanelAnalyticsService from "../../../services/Analytics/MixPanelAnalyticsService";
import { AnalyticsEvent } from "../../../services/Analytics/AnalyticsConstants";
import AuthSelectors from "./AuthSelectors";
import { Dispatch } from "../../store/store";
import { StateModules } from "../types";
import {AnalyticsAccountEvents} from "../../../services/Analytics/AnalyticsAccountEvents";

export interface IAuthModel extends Model {
  name: StateModules;
  state: IAuthState;
  reducers: IAuthReducers;
  effects: IAuthEffects;
}

export type IAuthState = {
  didRefresh: boolean;
  user?: IUserAuthData;
  magicLink?: {
    sending: boolean;
    email: string;
    error?: string;
  };
  failedSigninWithMagicLink?: boolean;
  isPeandingLead? : boolean;
};

export interface IUserAuthData {
  uid: string | null;
  email: string | null;
  displayName: string | null;
}

export type IAuthReducers = {
  updateAuthSucceeded: (state: IAuthState, user: IUserAuthData) => IAuthState;
  signOutSucceeded: (state: IAuthState) => IAuthState;
  magicLinkSending: (state: IAuthState, email: string) => IAuthState;
  magicLinkSent: (state: IAuthState) => IAuthState;
  magicLinkFailedSending: (state: IAuthState, error: string) => IAuthState;
  failedSigninWithMagicLink: (state: IAuthState) => IAuthState;
  setPendingLead: (state: IAuthState) => IAuthState;
};

export type IAuthEffects = {
  signOut: (params: {}, state: IState) => void;
  sendSignUpMagicLink: (email: string, state: IState) => void;
  finishSignUpWithLink: (link: string, state: IState) => void;
  updateAuth: (user?: IUserAuthData) => void;
};

export type IAuthDispatch = {
  updateAuthSucceeded: (user?: IUserAuthData) => void;
  updateAuth: (user?: IUserAuthData) => void;
  signOut: () => void;
  signOutSucceeded: () => void;
  magicLinkSending: (email: string) => void;
  magicLinkSent: () => void;
  magicLinkFailedSending: (error: string) => void;
  sendSignUpMagicLink: (email: string) => void;
  finishSignUpWithLink: (link: string) => void;
  failedSigninWithMagicLink: () => void;
  setPendingLead: () => void;
};

export const Auth: IAuthModel = {
  name: "auth",
  state: {
    didRefresh: false
  },
  reducers: {
    updateAuthSucceeded: (state, user) => ({
      ...state,
      didRefresh: true,
      user
    }),
    signOutSucceeded: state => ({
      ...state,
      user: undefined,
      magicLink: undefined
    }),
    magicLinkSending: (state, email) => ({
      ...state,
      magicLink: {
        email,
        sending: true,
        error: undefined
      },
      failedSigninWithMagicLink: undefined
    }),
    magicLinkSent: state => ({
      ...state,
      magicLink: {
        ...state.magicLink!,
        sending: false,
        error: undefined
      }
    }),
    magicLinkFailedSending: (state, error): IAuthState => ({
      ...state,
      magicLink: {
        ...state.magicLink!,
        sending: false,
        error
      }
    }),
    failedSigninWithMagicLink: state => ({
      ...state,
      failedSigninWithMagicLink: true
    }),
    setPendingLead: state => ({
      ...state,
      isPeandingLead: true
    })
  },
  effects: {
    signOut: async (params: {}, state: IState) => {
      try {
        MixPanelAnalyticsService.track(AnalyticsAccountEvents.logout);
        await FirebaseAuthService.signOut();
        Dispatch.auth.signOutSucceeded();
      } catch (error) {
        console.error(error);
      }
    },
    updateAuth: user => {
      Dispatch.auth.updateAuthSucceeded(user);
      MixPanelAnalyticsService.registerUser(user);
    },
    sendSignUpMagicLink: async (email: string, state: IState) => {
      try {
        Dispatch.auth.magicLinkSending(email);
        const signInResponse = await FirebaseAuthService.canSignIn(
          email
        );
        
        if (signInResponse.ok && !signInResponse.data.redirect) {
          await FirebaseAuthService.sendMagicLink(email);
          Dispatch.auth.magicLinkSent();
          LocalStorageService.setSignInEmail(email);
          // MixPanelAnalyticsService.track(AnalyticsEvent.SendMagicLink, { email });
          MixPanelAnalyticsService.track(AnalyticsEvent.SendMagicLink, { email });
        } else if (signInResponse.ok && signInResponse.data.redirect){
          Dispatch.auth.magicLinkFailedSending("Redirecting to pending screen");
          Dispatch.auth.setPendingLead()
        } else {
          const errorCode = signInResponse.error && signInResponse.error.code
            ? (signInResponse.error.code! as string)
            : "Error";
          console.log(`Setting error to ${errorCode}`)
          Dispatch.auth.magicLinkFailedSending(errorCode);
          MixPanelAnalyticsService.track(AnalyticsEvent.UninitializedDomain, {
            email,
            error: errorCode
          });
        }
      } catch (err) {
        const errorMsg = "Failed sending magic link";
        console.error(err);
        Dispatch.auth.magicLinkFailedSending(errorMsg);
        MixPanelAnalyticsService.trackError({
          email,
          message: errorMsg,
          error: `${err}`
        });
      }
    },
    finishSignUpWithLink: async (link: string, state: IState) => {
      if (AuthSelectors.isSignedIn(state.auth)) {
        console.log("User already signed in");
        LocalStorageService.removeSignInEmail();
        return;
      }

      let email = LocalStorageService.getSignInEmail();
      if (!email) {
        email = window.prompt("Please provide your email for confirmation");
      }

      if (!email) {
        console.error("no email");
        return;
      }

      try {
        await FirebaseAuthService.finishSignUpWithMagicLink(email, link);
        LocalStorageService.removeSignInEmail();
        MixPanelAnalyticsService.track(AnalyticsEvent.Login, { email });
      } catch (error) {
        console.error(error);
        Dispatch.auth.failedSigninWithMagicLink();
        MixPanelAnalyticsService.trackError({
          email,
          message: "Failed authenticating with magic link",
          error: `${error}`
        });
      }
    }
  }
};



// WEBPACK FOOTER //
// ./src/state/modules/auth/auth.ts